import MDivider from "@mui/material/Divider";
import MMenu, { menuClasses } from "@mui/material/Menu";
import MenuItem, { menuItemClasses } from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";
import { OverflownTooltip } from "Components/Generic/Tooltip/OverflownTooltip";

export const SingleUseBadge = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  padding: "4px 8px",

  borderRadius: "4px",
  background: colors.gray100,
  mixBlendMode: "multiply",

  color: colors.gray600,

  fontFamily: "Manrope",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: "0.5px",
}));

type ContainerProps = {
  fullWidth?: boolean;
  rounded?: boolean;
};

export const Container = styled("div", {
  shouldForwardProp: (props) => props !== "fullWidth" && props !== "rounded",
})<ContainerProps>(({ fullWidth, rounded }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: colors.white,
  width: fullWidth ? "100%" : "auto",
  borderRadius: rounded ? "4px" : undefined,
  overflow: "hidden",
}));

export const Title = styled("div", {
  shouldForwardProp: (props) => props !== "isVip",
})<{ isVip?: boolean }>(({ isVip }) => ({
  backgroundColor: isVip ? colors.warning50 : colors.brandBlue50,
  padding: "4px 12px",
  minHeight: "36px",
  display: "flex",
  alignItems: "center",
  gap: "4px",

  "& > span": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    letterSpacing: "0.1px",
    flex: 1,
  },
}));

export const Header = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "isVip";
  },
})(() => {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px 5px 12px",

    overflow: "hidden",
    color: colors.gray800,
    textOverflow: "ellipsis",
    fontFamily: "Manrope",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
    letterSpacing: "0.4px",
  };
});

export const CardHeaderDetailsSection = styled("div")(() => {
  return {
    padding: "8px 12px 0px 12px",
    width: "inherit",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };
});

export const Content = styled("div")({
  display: "flex",
  flexDirection: "column",
  padding: "12px",
});

export const ContentHeader = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "8px",
});

export const TaskType = styled(OverflownTooltip)({
  color: colors.brandBlue600,
  fontFamily: "Manrope",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  letterSpacing: "0.15px",
  paddingRight: "5px",
});

export const CustomerName = styled("span")({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
});

export const Address = styled("span")({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
});

export const QuantityValue = styled("span")({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
  marginTop: "8px",
});

export const Footer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "12px",
});

export const DateRange = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "disabled";
  },
})(({ disabled }: { disabled?: boolean }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "8px",
  padding: "4px 8px",
  border: `1px solid ${colors.gray200}`,
  width: "fit-content",
  borderRadius: "100px",
  cursor: disabled ? "not-allowed" : "pointer",

  backgroundColor: colors.gray50,
  "& span": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
    letterSpacing: "0.5px",
  },
}));

export const Badge = styled("div")({
  "& > div": {
    height: "24px",
  },
});

export const MemberLeaderWrapper = styled("div")({
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  gap: 4,
});

export const MemberLeaderLabel = styled("span")({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: 0.4,
});

export const MemberLeaderNameBadge = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: 100,
  backgroundColor: colors.gray100,
  "& span": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: 12,
  },
});

export const Menu = styled(MMenu)({
  [`&.${menuClasses.root}`]: {
    padding: "0px",
    paddingBottom: "4px",
  },
  [`&.${menuClasses.list}`]: {
    padding: "0px",
  },
  [`&.${menuClasses.paper}`]: {
    padding: "0px",
  },
  "& ul": {
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
  },
  "& li:nth-of-type(1)": {
    // paddingTop: "12px",
  },
  "& li:last-child": {
    // paddingBottom: "12px",
  },
});

type MenuOptionProps = {
  isError?: boolean;
};
export const MenuOption = styled(MenuItem, {
  shouldForwardProp: (props) => props !== "isError",
})<MenuOptionProps>(({ isError }) => ({
  color: isError ? colors.error600 : colors.gray600,
  fontFamily: "Manrope",
  fontSize: "14",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",

  [`&.${menuItemClasses.root}`]: {
    padding: "8px 12px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: "12px",
    justifyContent: "space-between",
    width: "100%",
  },
}));

export const LeftMenuOption = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "12px",
});

export const Divider = styled(MDivider)({
  marign: "4px 0px",
});

export const MenuOptionContent = styled("div")({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  columnGap: "12px",
});

export const CommentsIconWrapper = styled("div")(() => ({
  position: "relative",
}));

export const CardChip = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "8px",
  padding: "4px 8px",
  border: `1px solid ${colors.gray200}`,
  width: "fit-content",
  borderRadius: "100px",
  backgroundColor: colors.gray50,

  textTransform: "uppercase",
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: "15px",
  letterSpacing: "0.5px",
});
